import React, { useState, useEffect, useRef, useContext } from 'react';
import { Search, X } from 'lucide-react';
import Context from '../../../../../Context/Context';

export const SearchBar = ({
  onSearchResults,
  searchTerm,
  setSearchTerm
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionRef = useRef(null);
  const inputRef = useRef(null);
  const { videos, isDarkMode = true } = useContext(Context);

  // Generate suggestions from local videos data
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.trim().length > 0) {
        generateSuggestions(searchTerm);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        onSearchResults(null); // Reset to show all videos
      }
    }, 300); // Reduced debounce time for local filtering

    return () => clearTimeout(handler);
  }, [searchTerm, videos]);

  // Handle clicks outside of suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate suggestions from local videos data
  const generateSuggestions = (term) => {
    if (!videos || videos.length === 0) {
      setSuggestions([]);
      return;
    }

    const searchTerm = term.toLowerCase();
    const uniqueSuggestions = new Set();

    videos.forEach(video => {
      // Search in song name
      if (video.songName && video.songName.toLowerCase().includes(searchTerm)) {
        uniqueSuggestions.add(video.songName);
      }
      // Search in choreographer name
      if (video.choreographer && video.choreographer.toLowerCase().includes(searchTerm)) {
        uniqueSuggestions.add(video.choreographer);
      }
      // Search in title
      if (video.title && video.title.toLowerCase().includes(searchTerm)) {
        uniqueSuggestions.add(video.title);
      }
    });

    const suggestions = Array.from(uniqueSuggestions).slice(0, 10); // Limit to 10 suggestions
    setSuggestions(suggestions);
    setShowSuggestions(true);
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    
    // Filter videos locally based on the selected suggestion
    const filteredVideos = videos.filter(video => 
      video.songName?.toLowerCase().includes(suggestion.toLowerCase()) ||
      video.choreographer?.toLowerCase().includes(suggestion.toLowerCase()) ||
      video.title?.toLowerCase().includes(suggestion.toLowerCase())
    );
    
    onSearchResults(filteredVideos);
  };

  // Handle clearing search
  const handleClearSearch = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    onSearchResults(null);
  };

  return (
    <div className="relative w-full" ref={suggestionRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search videos by song name"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!showSuggestions) setShowSuggestions(true);
          }}
          onFocus={() => {
            if (searchTerm.trim().length > 0) setShowSuggestions(true);
          }}
          className={`w-full pl-10 pr-10 py-2.5 rounded-lg transition-all duration-200 text-xs md:text-sm ${
            isDarkMode
              ? 'border-none bg-[#1a1a1a] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
              : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
          }`}
        />
        
        {/* Search Icon */}
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <Search className={`w-4 h-4 md:w-5 md:h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
        </div>

        {/* Clear Button */}
        {searchTerm && (
          <button
            onClick={handleClearSearch}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${
              isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className={`absolute z-50 w-full mt-2 rounded-lg shadow-xl max-h-60 overflow-y-auto border ${
          isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
        }`}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionSelect(suggestion)}
              className={`px-4 py-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                isDarkMode
                  ? 'hover:bg-[#2a2a2a] border-[#2a2a2a]'
                  : 'hover:bg-gray-50 border-gray-100'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Search className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{suggestion}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {showSuggestions && searchTerm.trim().length > 0 && suggestions.length === 0 && (
        <div className={`absolute z-50 w-full mt-2 rounded-lg shadow-xl border ${
          isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
        }`}>
          <div className={`px-4 py-3 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No results found for "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
};